<script>
import ResizablePanel        from './ResizablePanel.vue';
import MidiMonitorChart      from './MidiMonitorChart.vue';
import MidiMonitorDeviceSelect from './MidiMonitorDeviceSelect.vue';

import MidiStreamStore       from '../js/MidiStreamStore.js';
import MidiAccess            from '../js/MidiAccess.js';

export default {
  name: 'MidiMonitorPanel',

  components: { ResizablePanel, MidiMonitorChart, MidiMonitorDeviceSelect },

  inject: ['midiMonitor'],

  props: {
    settings: {
      type: Object,
      required: false,
      default: null,
    },
    connectedDevice: {
      type: Object,
      required: false,
      default: null,
    },
    collapsible: {
      type: Boolean,
      default: true,
    },
    defaultHeight: {
      type: Number,
      default: 180,
    },
  },

  data() {
    return {
      // Populated by MidiStreamStore's onChange callback each tick
      activeKeys: [],
      streams:    {},
      buckets:    {},

      // Populated by MidiAccess callbacks
      midiInputs:      [],
      selectedInputId: null,
      midiError:       null,

      webMidiSupported: !!navigator.requestMIDIAccess,
    };
  },

  computed: {
    highlightedKey() {
      return this.midiMonitor.key;
    },

    effectTypeMap() {
      return this.midiMonitor.effectTypeMap ?? {};
    },
  },

  watch: {
    connectedDevice(newDevice) {
      this._wireDevice(newDevice);
    },

    highlightedKey(newKey) {
      if (!newKey) return;
      this.$nextTick(() => {
        const el = this.$el.querySelector(`[data-chart-key="${newKey}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
  },

  mounted() {
    this._store = new MidiStreamStore({
      onChange: () => this._syncFromStore(),
    });

    this._midi = new MidiAccess({
      onMessage:      (ch, cc, val) => this._store.record(ch, cc, val),
      onInputsChange: (inputs, selectedId) => {
        this.midiInputs      = inputs;
        this.selectedInputId = selectedId;
      },
      onError: (msg) => { this.midiError = msg; },
    });

    // Expose clearAll to the shared midiMonitor so Patches can trigger it.
    this.midiMonitor.clearAllStreams = (pauseMs) => this._store.clearAll(pauseMs);

    this._store.start();
    this._midi.init();

    // Wire the initial device (handles the on-device editor case where the
    // component mounts after connection is already established).
    this._wireDevice(this.connectedDevice);
  },

  beforeUnmount() {
    this._store.destroy();
    this._midi.destroy();
  },

  methods: {
    _wireDevice(device) {
      this._midi.removeVirtualInput('midulate-wifi');
      if (device?.onMidiCCBatch) {
        this._midi.addVirtualInput('midulate-wifi', 'Midulate (WiFi)');
        device.onMidiCCBatch((messages) => {
          for (const m of messages) {
            this._midi?.feedVirtualMessage('midulate-wifi', m.channel, m.cc, m.value);
          }
        });
      }
    },

    _syncFromStore() {
      this.activeKeys = this._store.activeKeys;
      this.buckets    = this._store.buckets;

      // Snapshot the mutable store stream objects into plain Vue-owned objects
      // so that latestValue (and other fields) are tracked by Vue's reactivity.
      // Simply copying the reference isn't enough — Vue can't observe in-place
      // mutations on objects it doesn't own.
      const snap = {};
      for (const key of this.activeKeys) {
        const s = this._store.streams[key];
        snap[key] = { channel: s.channel, cc: s.cc, latestValue: s.latestValue };
      }
      this.streams = snap;
    },

    selectInput(id) {
      this.selectedInputId = id;
      this._midi.selectInput(id);
    },

    refreshInputs() {
      this._midi.refresh();
    },
  },
};
</script>

<template>
  <ResizablePanel
    class="midi-monitor-panel"
    css-var="--midi-panel-height"
    :default-height="defaultHeight"
    :min-height="80"
    :max-height="600"
    :header-height="44"
    :resizable="collapsible"
  >
    <template #default="{ collapsed, toggleCollapsed }">

      <!-- Header -->
      <div class="midi-monitor-panel__header">
        <span class="midi-monitor-panel__title">MIDI Monitor</span>

        <button
          v-if="collapsible"
          class="midi-monitor-panel__icon-btn"
          :title="collapsed ? 'Expand monitor' : 'Collapse monitor'"
          @click="toggleCollapsed"
        >
          {{ collapsed ? '▲' : '▼' }}
        </button>

        <MidiMonitorDeviceSelect
          v-if="!collapsed && webMidiSupported"
          :midi-inputs="midiInputs"
          :selected-input-id="selectedInputId"
          :midi-error="midiError"
          :midi-access="!midiError"
          @update:selected-input-id="selectInput($event)"
          @refresh="refreshInputs"
        />
      </div>

      <!-- Charts -->
      <div v-if="!collapsed" class="midi-monitor-panel__charts">
        <div v-if="activeKeys.length === 0" class="midi-monitor-panel__empty">
          Waiting for CC messages&hellip;
        </div>

        <MidiMonitorChart
          v-for="key in activeKeys"
          :key="key"
          :data-chart-key="key"
          :chart-key="key"
          :stream="streams[key]"
          :buckets="buckets[key] ?? []"
          :settings="settings"
          :highlighted="highlightedKey === key"
          :dimmed="highlightedKey !== null && highlightedKey !== key"
          :effect-type="effectTypeMap[key] ?? null"
        />
      </div>

    </template>
  </ResizablePanel>
</template>
